import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import HeaderView from "../../components/admin/headerView";
import { useTranslation } from "react-i18next";
import { APP_CONFIG } from "../../constants/constants";
import { useFormik } from "formik";
import {
  whiteLabelIntialValues,
  whiteLabelValidationSchema,
} from "./validation";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldInput,
  AdminFormFieldIntermediateCheckbox,
  AdminFormFieldSubmit,
  AdminFormFieldTextarea,
  TSelectboxOption,
} from "../../components/admin/formFields";
import ImageSection from "./image-section";
import FormActionButtons from "../../components/admin/formActionButtons";
import FillAllRequiredFieldsCaption from "../../components/fillAllRequiredFieldsCaption";
import {
  useCreateWhiteLabelConfigMutation,
  useDeleteWhiteLabelConfigMutation,
  useGetWhiteLabelConfigListQuery,
  useGetWhiteLabelConfigQuery,
  useUpdateWhiteLabelConfigMutation,
} from "../../api/network/adminApiServices";
import { WhiteLabelConfig } from "../../api/types/WhiteLabelConfig";
import ImgReference from "../../assets/svg/reference.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  setListingQueryParams,
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../api/store/commonSlice";
import ModalReference from "./modalReference";
import svgIcons from "../../assets/svg/svgIcons";
import { useNavigate, useParams } from "react-router-dom";
import ModalDeleteDomain from "./modalDeleteDomain";
import AppSearchBox from "../../components/searchBox";
import { useDebouncedCallback } from "use-debounce";
import AdminListingColumnItem from "../../components/adminListingColumnItem";
import { routeUrls } from "../../navigation/routeUrls";
import Pagination, { TPaginationSelected } from "../../components/pagination";

const colorPresets = [
  {
    id: "preset_def",
    label: "Default",
    colors: ["#005B90", "#0E88FF", "#C2DCF2", "#C2DCF266"],
  },
  {
    id: "preset_1",
    label: "Preset 1",
    colors: ["#30746C", "#3F978C", "#52CBBD", "#BCF1EB"],
  },
  {
    id: "preset_2",
    label: "Preset 2",
    colors: ["#101755", "#274EB1", "#526ECB", "#BCCCF1"],
  },
];

enum ImageType {
  brandLogo = 1,
  favIcon = 2,
  background = 3,
}

const ScreenWhiteLabelSettings = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "whiteLabeling.whiteLabelSettings",
  });
  const { t: tParent } = useTranslation("translation", {
    keyPrefix: "whiteLabeling",
  });
  const { domainId } = useParams<{ domainId: any }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(
    domainId || null
  );

  const [userCanEdit, setUserCanEdit] = useState(domainId ? false : true); // Default editing disabled if domainId is present
  // Images
  const [brandLogo, setBrandLogo] = useState<File | null>(null);
  const [favIcon, setFavIcon] = useState<File | null>(null);
  const [background, setBackground] = useState<File | null>(null);
  const [brandingColors, setBrandingColors] = useState({
    color_dark: "#304674",
    color_bright: "#005B90",
    color_pale: "#439BCF",
    color_faded: "#C2DCF2",
  });
  // For preset colors
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const darkRef = useRef("#304674");
  const brightRef = useRef("#005B90");
  const paleRef = useRef("#439BCF");
  const fadeRef = useRef("#C2DCF2");

  const { data: dataConfig, isLoading: isFetchingConfigData } =
    useGetWhiteLabelConfigQuery(selectedDomainId ?? "", {
      skip: !selectedDomainId,
    });
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { whiteLabeling: whiteLabelingQueryParams } = listingQueryParams;
  // fetching listing data
  const {
    data: dataWhiteLabelConfig,
    isFetching: isFetchingListingData,
    error,
  } = useGetWhiteLabelConfigListQuery(whiteLabelingQueryParams);
  const { count, next, previous, results } = dataWhiteLabelConfig || {};

  const [createApiTrigger, { isLoading: isCreatingConfig }] =
    useCreateWhiteLabelConfigMutation();
  const [updateApiTrigger, { isLoading: isUpdatingConfig }] =
    useUpdateWhiteLabelConfigMutation();
  const [deleteApiTrigger, { isLoading: isDeletingConfig }] =
    useDeleteWhiteLabelConfigMutation();

  // preparing query params
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        whiteLabeling: {
          ...whiteLabelingQueryParams,
          page: 1,
          search: value,
        },
      })
    );
  }, 500);

  // formik
  const [formikValuesReady, setFormikValuesReady] = useState<boolean>(false);
  const formik = useFormik({
    initialValues: whiteLabelIntialValues,
    validationSchema: whiteLabelValidationSchema,
    onSubmit: (values) => {
      // prepare payload
      // Remove trailing slash from domain if present
      const sanitizedDomain = values.domain.endsWith("/")
        ? values.domain.slice(0, -1)
        : values.domain;
      const data: Partial<WhiteLabelConfig> = {
        domain: sanitizedDomain,
        brand_name: values.brand_name,
        color_dark: values.color_dark,
        color_bright: values.color_bright,
        color_pale: values.color_pale,
        color_faded: values.color_faded,
        landing_page_heading: values.landing_page_heading,
        landing_page_content: values.landing_page_content,
        logo_image: values.logo_image ?? null,
        meta_title: values.meta_title,
        meta_description: values.meta_description,
      };
      if (values?.login_screen_logo instanceof File) {
        // If a file is selected, we need to upload it
        data.login_screen_logo = values.login_screen_logo;
      }
      if (values.landing_page_image instanceof File) {
        // If a file is selected, we need to upload it
        data.landing_page_image = values.landing_page_image;
      }
      if (values.favicon instanceof File) {
        // If a file is selected, we need to upload it
        data.favicon = values.favicon;
      }
      if (APP_CONFIG.DEBUG.ALL) console.log("White Labeldata", data);
      if (dataConfig && dataConfig?.id) {
        // Update config
        updateApiTrigger({ id: dataConfig.id, data })
          .then((res) => {
            if (res && res?.data?.id) {
              toast.success("Config updated successfuly");
              goBack(); // Go back to previous page
            }
          })
          .catch((error) => {
            toast.error(error);
          });
      } else {
        // Create Config
        createApiTrigger(data)
          .then((res) => {
            if (res && res?.data?.id) {
              toast.success("Config stored successfuly");
              setUserCanEdit(!userCanEdit); // Reset edit mode
              if (!domainId) {
                // If domainId is created, navigate to listing page
                clearForm();
                goBack(); // Go back to previous page
              }
            } else if (res?.error) {
              const error = res?.error as any;
              if (error?.data?.domain && error?.data?.domain[0])
                toast.error(error?.data?.domain[0]);
            }
          })
          .catch((error) => {
            toast.error(error);
          });
      }
    },
  });
  const { values, errors, touched, handleChange, handleBlur, handleSubmit } =
    formik;

  // Pre-fill formik data
  useEffect(() => {
    if (dataConfig) {
      formik.setValues({
        domain: dataConfig.domain || "",
        brand_name: dataConfig.brand_name || "",
        color_dark: dataConfig.color_dark || "#304674",
        color_bright: dataConfig.color_bright || "#005B90",
        color_pale: dataConfig.color_pale || "#439BCF",
        color_faded: dataConfig.color_faded || "#C2DCF2",
        login_screen_logo: dataConfig?.login_screen_logo || "",
        landing_page_heading: dataConfig.landing_page_heading || "",
        landing_page_content: dataConfig.landing_page_content || "",
        landing_page_image: null,
        logo_image: null,
        favicon: null,
        meta_title: dataConfig.meta_title || "",
        meta_description: dataConfig.meta_description || "",
      });
      setBrandingColors({
        color_dark: dataConfig.color_dark || "#304674",
        color_bright: dataConfig.color_bright || "#005B90",
        color_pale: dataConfig.color_pale || "#439BCF",
        color_faded: dataConfig.color_faded || "#C2DCF2",
      });
      darkRef.current = dataConfig.color_dark || "#304674";
      brightRef.current = dataConfig.color_bright || "#005B90";
      paleRef.current = dataConfig.color_pale || "#439BCF";
      fadeRef.current = dataConfig.color_faded || "#C2DCF2";

      colorPresets?.forEach((prestObj) => {
        if (
          prestObj.colors[0] === dataConfig.color_dark &&
          prestObj.colors[1] === dataConfig.color_bright &&
          prestObj.colors[2] === dataConfig.color_pale &&
          prestObj.colors[3] === dataConfig.color_faded
        ) {
          setSelectedPreset(prestObj.id);
        }
      });
      setFormikValuesReady(true);
    }
  }, [dataConfig]);

  const onImageSelected = (type: ImageType, selectedFile: File) => {
    switch (type) {
      case ImageType.brandLogo:
        setBrandLogo(selectedFile);
        formik.setFieldValue("login_screen_logo", selectedFile);
        break;
      case ImageType.favIcon:
        setFavIcon(selectedFile);
        formik.setFieldValue("favicon", selectedFile);
        break;
      case ImageType.background:
        setBackground(selectedFile);
        formik.setFieldValue("landing_page_image", selectedFile);
        break;
      default:
        break;
    }
  };

  const onColorPresetSelected = (presetId: string) => {
    setSelectedPreset(presetId);
    const selected = colorPresets.find((p) => p.id === presetId);
    if (selected) {
      setBrandingColors({
        color_dark: selected.colors[0],
        color_bright: selected.colors[1],
        color_pale: selected.colors[2],
        color_faded: selected.colors[3],
      });
      formik.setFieldValue("color_dark", selected.colors[0]);
      formik.setFieldValue("color_bright", selected.colors[1]);
      formik.setFieldValue("color_pale", selected.colors[2]);
      formik.setFieldValue("color_faded", selected.colors[3]);
    }
  };

  const ColorPreset = () => {
    return (
      <div className="grid grid-cols-12 col-span-12 gap-4">
        {colorPresets.map((preset) => (
          <div
            key={preset.id}
            className="grid grid-cols-6 col-span-10 items-center"
          >
            <AdminFormFieldIntermediateCheckbox
              id={preset.id}
              label={preset.label}
              type="check"
              name={preset.id}
              checked={preset.id === selectedPreset}
              onChange={() => onColorPresetSelected(preset.id)}
              customWrapperClass="col-span-1"
              disabled={!userCanEdit}
            />
            <div className="w-40 h-8 grid grid-cols-4 col-span-4">
              {preset.colors.map((color, idx) => (
                <div
                  key={idx}
                  className="w-10 h-full col-span-1"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleCustomColorChange = (
    colorKey: keyof typeof brandingColors,
    value: string
  ) => {
    if (brandingColors[colorKey] === value) {
      // Color was not changes, don't make any changes
      return;
    }
    setBrandingColors((prev) => ({
      ...prev,
      [colorKey]: value,
    }));
    formik.setFieldValue(colorKey, value);
    setSelectedPreset(null); // Remove preset color selection
  };

  const clearForm = () => {
    formik.setValues({
      domain: "",
      brand_name: "",
      login_screen_logo: null,
      landing_page_heading: "",
      landing_page_content: "",
      landing_page_image: null,
      logo_image: "",
      favicon: null,
      meta_title: "",
      meta_description: "",
      color_dark: "",
      color_bright: "",
      color_pale: "",
      color_faded: "",
    });
  };

  const goBack = useCallback(() => navigate(-1), [navigate]);

  const onReferenceClick = useCallback(() => {
    dispatch(
      setModalsData({ ...modalsState, showReferenceLandingModal: true })
    );
  }, []);

  // const onResetToDefaultClick = useCallback(() => {
  //   formik.setValues({
  //     domain: "",
  //     brand_name: "",
  //     login_screen_logo: null,
  //     landing_page_heading: "",
  //     landing_page_content: "",
  //     landing_page_image: null,
  //     logo_image: "",
  //     favicon: null,
  //     meta_title: "",
  //     meta_description: "",
  //     color_dark: "#304674",
  //     color_bright: "#005B90",
  //     color_pale: "#439BCF",
  //     color_faded: "#C2DCF2",
  //   });
  //   setBrandingColors({
  //     color_dark: "#304674",
  //     color_bright: "#005B90",
  //     color_pale: "#439BCF",
  //     color_fade: "#C2DCF2",
  //   });
  // }, [formik]);

  const onDeleteClick = () =>
    dispatch(setModalsData({ ...modalsState, showDeleteDomainModal: true }));

  const onDeleteConfirm = () => {
    if (dataConfig && dataConfig?.id) {
      deleteApiTrigger(dataConfig.id)
        .then((res) => {
          if (res) {
            toast.success(tParent("toast.delete_success"));
            goBack(); // Go back to previous page
          }
        })
        .catch((error) => {
          toast.error(tParent("toast.delete_failed"));
        });
    } else {
      toast.error("No domain found to delete");
    }
  };

  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              type: "button",
              variant: "primary",
              label:
                isCreatingConfig || isUpdatingConfig ? (
                  <img
                    src={svgIcons.LoadingAnimation}
                    alt="Loading animation"
                    className={`size-5`}
                  />
                ) : userCanEdit ? (
                  tMain("update")
                ) : (
                  tMain("edit")
                ),
              onClick: userCanEdit
                ? handleSubmit
                : () => setUserCanEdit(!userCanEdit),
              disabled: userCanEdit && !!Object.keys(errors).length, //|| getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_MAINTENANCE) < ROLE_ACCESS_LEVELS.WRITE
            },
          ]}
        />
      </>
    );
  }, [userCanEdit, errors, isUpdatingConfig, isCreatingConfig]);

  const ManualColorSelection = () => {
    const pickerStyle = "h-12 w-20 bg-white cursor-pointer rounded-lg border-0";
    const textStyle =
      "ml-2 w-28 p-2 border border-gray-200 rounded-lg disabled:opacity-50";
    return (
      <div className="grid grid-cols-12 gap-4 col-span-12">
        <div className="grid grid-cols-6 gap-4 col-span-12 mb-2">
          <div className="flex items-center col-span-3">
            <input
              type="color"
              value={darkRef.current}
              onChange={(e) => {
                darkRef.current = e.target.value;
              }}
              onBlur={(e) => {
                handleCustomColorChange("color_dark", darkRef.current);
              }}
              disabled={!userCanEdit}
              className={pickerStyle}
            />
            <input
              type="text"
              value={darkRef.current}
              disabled={true}
              className={textStyle}
            />
          </div>
          <div className="flex items-center col-span-3">
            <input
              type="color"
              value={brightRef.current}
              onChange={(e) => {
                brightRef.current = e.target.value;
              }}
              onBlur={(e) => {
                handleCustomColorChange("color_bright", brightRef.current);
              }}
              disabled={!userCanEdit}
              className={pickerStyle}
            />
            <input
              type="text"
              value={brightRef.current}
              disabled={true}
              className={textStyle}
            />
          </div>
        </div>
        <div className="grid grid-cols-6 gap-4 col-span-12 mb-2">
          <div className="flex items-center col-span-3">
            <input
              type="color"
              value={paleRef.current}
              onChange={(e) => {
                paleRef.current = e.target.value;
              }}
              onBlur={(e) => {
                handleCustomColorChange("color_pale", paleRef.current);
              }}
              disabled={!userCanEdit}
              className={pickerStyle}
            />
            <input
              type="text"
              value={paleRef.current}
              disabled={true}
              className={textStyle}
            />
          </div>
          <div className="flex items-center col-span-3">
            <input
              type="color"
              value={fadeRef.current}
              onChange={(e) => {
                fadeRef.current = e.target.value;
              }}
              onBlur={(e) => {
                handleCustomColorChange("color_faded", fadeRef.current);
              }}
              disabled={!userCanEdit}
              className={pickerStyle}
            />
            <input
              type="text"
              value={fadeRef.current}
              disabled={true}
              className={textStyle}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={() => navigate(-1)}
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="flex items-center font-sm text-accent-dark text-sm leading-2">
          {t("sub_heading")}
        </div>
        <div className="flex items-center mt-2">
          {!dataConfig ? (
            <div className="flex-grow"></div>
          ) : (
            <div className="font-bold text-lg leading-6 flex-grow">
              {t("listing_heading")}
            </div>
          )}
          <div className="flex items-center gap-4">
            <>
              <div className="flex-grow"></div>
              {userCanEdit && !!Object.keys(errors).length && (
                <FillAllRequiredFieldsCaption />
              )}
              {userCanEdit && (
                <div className="w-24">
                  <AdminFormFieldSubmit
                    type="button"
                    variant="primary-alt"
                    label={tMain("cancel")}
                    onClick={goBack}
                    disabled={
                      isFetchingConfigData ||
                      isCreatingConfig ||
                      isUpdatingConfig ||
                      isDeletingConfig
                    }
                  />
                </div>
              )}
              {dataConfig ? (
                <>
                  <div className="w-24">{MemoizedFormActionButtons}</div>
                  <div className="w-32">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="danger"
                      label={
                        isDeletingConfig ? (
                          <img
                            src={svgIcons.LoadingAnimation}
                            alt="Loading animation"
                            className={`size-5`}
                          />
                        ) : (
                          tMain("delete")
                        )
                      }
                      onClick={onDeleteClick}
                      disabled={isFetchingConfigData}
                    />
                  </div>
                </>
              ) : (
                <div className="w-24">
                  <FormActionButtons
                    buttons={[
                      {
                        type: "button",
                        variant: "primary",
                        label: isCreatingConfig ? (
                          <img
                            src={svgIcons.LoadingAnimation}
                            alt="Loading animation"
                            className={`size-5`}
                          />
                        ) : (
                          tMain("save")
                        ),
                        onClick: handleSubmit,
                        disabled:
                          isCreatingConfig || !!Object.keys(errors).length,
                      },
                    ]}
                  />
                </div>
              )}
            </>
          </div>
        </div>
        <div className="lg:grid lg:grid-cols-12 py-2 gap-4">
          <div
            className={`hidden xl:block lg:col-span-3 space-y-4${isFetchingListingData ? " opacity-40 pointer-events-none" : ""}${!dataConfig ? " xl:hidden" : ""}`}
          >
            <AppSearchBox
              placeholder={tParent("search_placeholder")}
              initialValue={whiteLabelingQueryParams?.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSetSearchKeyword(e.target.value)
              }
              onClear={() => {
                debouncedSetSearchKeyword("");
              }}
              wrapperClassName="mt-4"
            />
            <div className="max-h-admins overflow-y-auto">
              {results?.map((item: WhiteLabelConfig, index: number) => (
                <AdminListingColumnItem
                  key={index}
                  selected={parseInt(selectedDomainId ?? "0") === item.id}
                  onClick={() => {
                    navigate(
                      `${routeUrls.dashboardChildren.whiteLabelChildren.whitelabel_settings}/${item.id}`
                    );
                    setSelectedDomainId(item?.id?.toString() ?? "");
                  }}
                  title={item.brand_name || item.domain}
                  description={item.domain}
                  asideText={""}
                  bottomText={item.meta_title || ""}
                />
              ))}
            </div>
            {!isFetchingListingData && (
              <Pagination
                pageSize={whiteLabelingQueryParams.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      whiteLabeling: {
                        ...whiteLabelingQueryParams,
                        page: 1,
                        page_size: parseInt(`${e?.value}`),
                      },
                    })
                  );
                }}
                totalPages={
                  count
                    ? Math.ceil(count / whiteLabelingQueryParams.page_size)
                    : 1
                }
                forcePage={whiteLabelingQueryParams.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      whiteLabeling: {
                        ...whiteLabelingQueryParams,
                        page: data?.selected + 1,
                      },
                    })
                  );
                }}
                onlyPageChange={true}
                size={1}
              />
            )}
          </div>
          <div
            className={`${!dataConfig ? "lg:col-span-12" : "col-span-12 xl:col-span-9"}`}
          >
            <form>
              <div className="mt-4 pt-4 pb-8 grid grid-cols-12 gap-4 border-gray-100 border-2 rounded-lg shadow-lg">
                <div className="col-span-12 grid grid-cols-12 gap-4 px-4">
                  <div className="col-span-12 grid grid-cols-12">
                    <h2 className="text-xl font-semibold text-accent-blue-dark col-span-12">
                      Brand Identity
                    </h2>
                    <h4 className="text-sm text-gray-600 col-span-12 mb-4">
                      Set up your brand’s foundational elements.
                    </h4>
                  </div>
                  <AdminFormFieldInput
                    label={t("domain_prefix")}
                    placeholder={t("domain_prefix_subtitile")}
                    type="text"
                    id="domain"
                    name="domain"
                    value={values.domain}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.domain}
                    touched={touched.domain}
                    disabled={dataConfig ? true : !userCanEdit}
                    detailsFormField={true}
                    customWrapperClass="col-span-6"
                  />
                  <AdminFormFieldInput
                    label={t("brand_name")}
                    placeholder={t("brand_name_subtitile")}
                    type="text"
                    id="brand_name"
                    name="brand_name"
                    value={values.brand_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.brand_name}
                    touched={touched.brand_name}
                    disabled={!userCanEdit}
                    detailsFormField={true}
                    customWrapperClass="col-span-6"
                  />
                  <ImageSection
                    heading={t("brandLogo")}
                    onSelected={(file) =>
                      onImageSelected(ImageType.brandLogo, file)
                    }
                    wrapperClassName="col-span-12"
                    formError={
                      dataConfig?.login_screen_logo
                        ? ""
                        : errors.login_screen_logo
                    }
                    fileUrl={dataConfig?.login_screen_logo ?? null}
                    disabled={!userCanEdit}
                  />
                  <ImageSection
                    heading={t("fav_icon")}
                    onSelected={(file) =>
                      onImageSelected(ImageType.favIcon, file)
                    }
                    wrapperClassName="col-span-12"
                    formError={dataConfig?.favicon ? "" : errors.favicon}
                    fileUrl={dataConfig?.favicon ?? null}
                    disabled={!userCanEdit}
                  />

                  <div className="col-span-12 flex justify-between mb-4">
                    <h2 className="text-xl font-semibold text-accent-blue-dark">
                      Landing Page
                    </h2>
                    <span
                      className="text-blue-500 underline flex flex-row cursor-pointer items-center"
                      onClick={onReferenceClick}
                    >
                      <img src={ImgReference} alt="reference" />
                      <text className="ml-2 font-md">Reference Screen</text>
                    </span>
                  </div>

                  <AdminFormFieldInput
                    label={t("header_content")}
                    placeholder={t("limit_subtext")}
                    type="text"
                    id="landing_page_heading"
                    name="landing_page_heading"
                    value={values.landing_page_heading}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.landing_page_heading}
                    touched={touched.landing_page_heading}
                    disabled={!userCanEdit}
                    detailsFormField={true}
                    customWrapperClass="col-span-12"
                  />
                  <AdminFormFieldTextarea
                    label={t("description")}
                    placeholder={t("limit_subtext_lg")}
                    id="landing_page_content"
                    name="landing_page_content"
                    value={values.landing_page_content}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.landing_page_content}
                    touched={touched.landing_page_content}
                    disabled={!userCanEdit}
                    detailsFormField={true}
                    customWrapperClass="col-span-12"
                    height="h-32"
                  />

                  <ImageSection
                    heading={t("background_image")}
                    onSelected={(file) =>
                      onImageSelected(ImageType.background, file)
                    }
                    wrapperClassName="col-span-12"
                    formError={
                      dataConfig?.landing_page_image
                        ? ""
                        : errors.landing_page_image
                    }
                    fileUrl={dataConfig?.landing_page_image ?? null}
                    disabled={!userCanEdit}
                  />
                </div>

                <div className="col-span-12 grid grid-cols-12 gap-4 border-gray-200 border-t pt-4 px-4">
                  <div className="col-span-12 grid grid-cols-12">
                    <h2 className="text-xl font-semibold text-accent-blue-dark col-span-12 mb-4">
                      {t("color_theme")}
                    </h2>
                    <text className="text-sm font-semibold col-span-12 mb-4">
                      Preset
                    </text>
                    <ColorPreset />

                    <text className="text-sm font-semibold col-span-12 mt-8 mb-4">
                      Custom Colors
                    </text>
                    <ManualColorSelection />
                  </div>
                </div>

                <div className="col-span-12 grid grid-cols-12 gap-4 border-gray-200 border-t pt-4 px-4">
                  <h2 className="text-xl font-semibold text-accent-blue-dark col-span-12 mb-4 mt-2">
                    SEO and Metadata
                  </h2>
                  <AdminFormFieldInput
                    label={t("meta_title")}
                    placeholder={t("limit_subtext")}
                    type="text"
                    id="meta_title"
                    name="meta_title"
                    value={values.meta_title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.meta_title}
                    touched={touched.meta_title}
                    disabled={!userCanEdit}
                    detailsFormField={true}
                    customWrapperClass="col-span-12"
                  />

                  <AdminFormFieldTextarea
                    label={t("meta_description")}
                    placeholder={t("limit_subtext_lg")}
                    id="meta_description"
                    name="meta_description"
                    value={values.meta_description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.meta_description}
                    touched={touched.meta_description}
                    disabled={!userCanEdit}
                    detailsFormField={true}
                    customWrapperClass="col-span-12"
                    height="h-32"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ModalReference />
      <ModalDeleteDomain onDeleteConfirm={onDeleteConfirm} />
    </>
  );
};

export default ScreenWhiteLabelSettings;
